import { FC, MouseEvent, RefObject, useEffect, useMemo, useState } from 'react';
import { preferredName } from 'api/account/patients.type';
import useInsuranceCoverageInfo from 'api/insurance/useInsuranceCoverInfo';
import { Example, ModalContent } from 'app-pages/pages/consult/eligibility/styles';
import { NpInsuranceForm } from 'app-pages/pages/np/NpInsuranceForm';
import { BackArrowButton } from 'components/_global/back-button/BackArrowButton';
import { Animation } from 'components/animation/Animation';
import loadingAnimation from 'components/animation/data/loading.json';
import { SubmitButton } from 'components/core/button/Button';
import { useForm } from 'components/core/form';
import { ExternalLink } from 'components/core/link/Link';
import { Text } from 'components/core/text';
import { H3, H6 } from 'components/core/text/Text';
import { Select, StandardSelectOption } from 'components/form/Select';
import { InsuranceForm, InsuranceFormModel } from 'components/insurance/InsuranceForm';
import { FooterChildrenWrapper } from 'components/structure/footer/FooterChildrenWrapper';
import { FooterPortal } from 'components/structure/footer/FooterPortal';
import { useConsult } from 'contexts/ConsultContext';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { useSponsors } from 'contexts/SponsorsContext';
import { FeatureFlags, useFlag } from 'hooks/useFlag';
import Back from 'public/assets/img/eligibility/Back.svg';
import Front from 'public/assets/img/eligibility/Front.svg';
import { XCircle } from 'react-feather';
import ReactModal from 'react-modal';
import { useLocation, useNavigate } from 'react-router-dom';

import { InnerContent, LoadingIndicator } from './styles';

interface Props {
  handleSubmit: (data: InsuranceFormModel) => void;
  abandonEligibilityCheck: () => void;
  errorCount?: number;
  initialValues?: InsuranceFormModel;
  formRef: RefObject<HTMLFormElement>;
  loading: boolean;
}

export const EligibilityForm: FC<Props> = ({
  handleSubmit,
  errorCount,
  abandonEligibilityCheck,
  initialValues,
  formRef,
  loading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { consult } = useConsult();
  const {
    state: { account },
    isSelf,
  } = useCurrentUser();

  const showNewInsuranceForm = useFlag(FeatureFlags.NpShowNewInsuranceForm);

  const { data: insuranceCoverage } = useInsuranceCoverageInfo(consult?.patient?.id);

  const currentSponsorCoverageData = insuranceCoverage?.[0];

  const [showModal, setShowModal] = useState<boolean>(false);
  const handleModal = () => setShowModal(!showModal);
  const { sponsors, selectOptions: insuranceSelectOptions } = useSponsors();
  const localInitialValues = {
    memberFirstName: consult?.patient?.firstName,
    memberLastName: consult?.patient?.lastName,
    memberDob: consult?.patient?.dateOfBirth,
    phoneNumber: currentSponsorCoverageData?.insurancePhone,
    groupNumber: currentSponsorCoverageData?.groupId,
    ...initialValues,
    ...currentSponsorCoverageData,
  };

  const { getValues } = useForm();

  const defaultInsuranceProvider = useMemo(() => {
    const insuranceProviderName =
      localStorage.getItem('providerOptions') || account?.insuranceCarrier || 'Insurance Provider';
    return sponsors.find((i) => i.displayName === insuranceProviderName);
  }, [account?.insuranceCarrier, sponsors]);

  const [insuranceOption, setInsuranceOption] = useState<StandardSelectOption | null>(null);

  useEffect(() => {
    if (insuranceSelectOptions.length && !insuranceOption) {
      setInsuranceOption(insuranceSelectOptions.find((o) => o.label == defaultInsuranceProvider?.displayName) ?? null);
    }
  }, [defaultInsuranceProvider?.displayName, insuranceOption, insuranceSelectOptions]);

  const selectedSponsor = useMemo(() => {
    if (!insuranceOption) return null;
    return sponsors.find((s) => s.displayName === insuranceOption.label);
  }, [insuranceOption, sponsors]);

  const onSubmit = async (data: InsuranceFormModel) => {
    handleSubmit(data);
  };

  const clickSubmit = (e: MouseEvent) => {
    e.preventDefault();

    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  if (!consult) return null;

  const handleBackClick = (e: MouseEvent) => {
    e.preventDefault();

    if (location.state?.from && location.state?.from.startsWith('/account/add-member')) {
      navigate(`/account/add-member?patientId=${consult.patient.id}`, {
        replace: true,
      });
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      {loading && (
        <LoadingIndicator>
          <Animation source={loadingAnimation} className="h-[150px] sm:h-[200px]" />
          <H3 style={{ maxWidth: 688 }}>Checking your eligibility may take a minute. Thanks for your patience!</H3>
        </LoadingIndicator>
      )}
      <BackArrowButton onClick={handleBackClick} />
      <InnerContent className="pt-2">
        {consult?.patient?.npPlanOnly ? (
          <>
            <Text variant="heading3" bold data-testid="subheadline">
              Insurance
            </Text>
            <div className="mt-2 mb-10">
              <Text variant="text5" data-testid="provider-information">
                Please provide images of the front and back of {preferredName(consult.patient)}&apos;s{' '}
                <ExternalLink onClick={() => handleModal()}>
                  <Text variant="text5" bold>
                    insurance card
                  </Text>
                </ExternalLink>{' '}
                and verify the details:
              </Text>
            </div>
          </>
        ) : (
          <>
            <Text variant="heading3" bold data-testid="subheadline">
              Insurance for {isSelf ? 'your care' : preferredName(consult.patient)}
            </Text>
            <div className="mt-2 mb-10">
              <Text variant="text5" data-testid="provider-information">
                Please provide the following (found on your{' '}
                <ExternalLink onClick={() => handleModal()}>
                  <Text variant="text5" bold>
                    insurance card
                  </Text>
                </ExternalLink>
                )
              </Text>
            </div>
            <div className="mb-6">
              <Select
                id="sponsorId"
                name="sponsorId"
                data-testid="sponsorId"
                options={insuranceSelectOptions}
                label="Insurance Provider"
                openMenuOnFocus
                blurInputOnSelect
                classNamePrefix="select"
                value={insuranceOption}
                onChange={setInsuranceOption}
              />
            </div>
          </>
        )}
        {showNewInsuranceForm && consult?.patient?.npPlanOnly ? (
          <NpInsuranceForm
            handleSubmit={onSubmit}
            initialValues={localInitialValues}
            hidePayerId={selectedSponsor?.hidePayerId}
            hideGroupNumber={selectedSponsor?.hideGroupId}
            hideInsurancePhoneNumber={selectedSponsor?.hideInsurancePhoneNumber}
            hideMemberId={selectedSponsor?.hideMemberId}
            insuranceOption={insuranceOption}
            setInsuranceOption={setInsuranceOption}
            insuranceSelectOptions={insuranceSelectOptions}
          />
        ) : (
          <InsuranceForm
            handleSubmit={onSubmit}
            childName={preferredName(consult.patient)}
            initialValues={localInitialValues}
            hidePayerId={selectedSponsor?.hidePayerId}
            hideGroupNumber={selectedSponsor?.hideGroupId}
            hideInsurancePhoneNumber={selectedSponsor?.hideInsurancePhoneNumber}
            hideMemberId={selectedSponsor?.hideMemberId}
          />
        )}
      </InnerContent>
      <FooterPortal>
        <FooterChildrenWrapper>
          {!!errorCount && (
            <SubmitButton
              variant="secondary"
              onClick={(e) => {
                e.preventDefault();
                abandonEligibilityCheck();
              }}
              isLoading={loading}
            >
              Pay without insurance
            </SubmitButton>
          )}
          <SubmitButton isLoading={loading} onClick={clickSubmit} type="submit" data-testid="submit-button">
            Submit
          </SubmitButton>
        </FooterChildrenWrapper>
      </FooterPortal>
      <ReactModal
        isOpen={showModal}
        overlayClassName="modalOverlay"
        className="modalStyles"
        shouldCloseOnOverlayClick={true}
      >
        <ModalContent>
          <XCircle onClick={() => handleModal()} className="flex self-end mr-5 mt-5 cursor-pointer" />
          <H6 className="mt-5" data-testid="modal-title">
            <Example>Example</Example> <span>Insurance Card</span>
          </H6>
          <img src={Front} />
          <img src={Back} />
        </ModalContent>
      </ReactModal>
    </>
  );
};
